import { ViewModeActionHandle } from "./viewModeActionHandle";
import type {
	MaybeAsync,
	ViewModeActionDefinition,
	ViewModeDefinition,
	ViewModeDescriptor,
	ViewModeOperationResult,
	ViewModeTransition
} from "./view-modes-section-types";

export class ViewModeHandle implements ViewModeDescriptor {
	public readonly key: string;

	private actionHandles: ViewModeActionHandle[] = [];

	public constructor(public readonly mode: ViewModeDefinition) {
		this.key = mode.code;
		if(!this.key) {
			throw new Error("view-modes-mode requires a non-empty code.");
		}
	}

	public get code(): string {
		return this.mode.code;
	}

	public get isAvailable(): boolean {
		return this.mode.available !== false;
	}

	public get isDisabled(): boolean {
		return this.mode.disabled === true;
	}

	public get isLoading(): boolean {
		return this.mode.loading === true;
	}

	public get isSelectable(): boolean {
		return this.isAvailable && !this.isDisabled;
	}

	public get actions(): ViewModeActionHandle[] {
		return this.actionHandles.filter(action => action.isVisible);
	}

	public isDirty(): boolean {
		return this.mode.dirtyCheck?.() === true;
	}

	public enter(transition: ViewModeTransition): MaybeAsync<ViewModeOperationResult> {
		return this.mode.onEnter?.({ $transition: transition });
	}

	public leave(transition: ViewModeTransition): MaybeAsync<ViewModeOperationResult> {
		return this.mode.onLeave?.({ $transition: transition });
	}

	public addAction(action: ViewModeActionDefinition): ViewModeActionHandle {
		if(this.actionHandles.some(handle => handle.code === action.code)) {
			throw new Error(`Duplicate view-mode-action code '${action.code}' in mode '${this.code}'.`);
		}

		const handle = new ViewModeActionHandle(action, this);
		this.actionHandles.push(handle);

		return handle;
	}

	public isSame(mode?: ViewModeHandle | string | null): boolean {
		if(mode === undefined || mode === null) {
			return false;
		}

		return mode instanceof ViewModeHandle
			? mode === this || mode.code === this.code
			: mode === this.code;
	}
}
